import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as LoginActions from './login.actions';
import { LoginService } from './login.service';
import { map, switchMap, catchError, withLatestFrom, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectLoginState } from './login.selectors';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';
import { IAuthenticatedModel } from './login.models';

@Injectable()
export class LoginEffects {
  constructor(
    private actions$: Actions,
    private loginService: LoginService,
    private store: Store,
    private cookieService: CookieService,
  ) {}

  private areCookiesSet(): boolean {
    return (
      this.cookieService.check(environment.UserGuid) &&
      this.cookieService.check(environment.BusinessPlanGuid) &&
      this.cookieService.check(environment.ProductGuid) &&
      this.cookieService.check(environment.CompanyGuid)
    );
  }

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActions.login),
      withLatestFrom(this.store.select(selectLoginState)),
      switchMap(([{ payload }, state]) => {
        const email = payload.UserName;
        const cachedResponse = state?.authenticated?.[email];
        const cookiesExist = this.areCookiesSet();
        if (cookiesExist) {
          return of(LoginActions.loginSuccess({ email, response: cachedResponse }));
        }

        return this.loginService.login(payload).pipe(
          tap((response: IAuthenticatedModel) => {
            this.cookieService.set(environment.UserGuid, response.UserGuid);
            this.cookieService.set(environment.BusinessPlanGuid, response.BusinessPlanGuid);
            this.cookieService.set(environment.ProductGuid, response.ProductGuid);
            this.cookieService.set(environment.CompanyGuid, response.CompanyGuid);
          }),
          map((response: IAuthenticatedModel) => LoginActions.loginSuccess({ email, response })),
          catchError(() => {
            return of(LoginActions.loginFailure({ error: 'views.globals.serverError' }));
          }),
        );
      }),
    ),
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoginActions.logout),
        tap(() => {
          this.cookieService.delete(environment.UserGuid);
          this.cookieService.delete(environment.BusinessPlanGuid);
          this.cookieService.delete(environment.ProductGuid);
          this.cookieService.delete(environment.CompanyGuid);
          // Agrega aquí cualquier limpieza adicional si es necesario
        }),
      ),
    { dispatch: false },
  );
}
